import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import MarketingShell from "@/components/MarketingShell";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Building2, SearchX, Stethoscope, Hospital } from "lucide-react";

export default function BrowseClinics() {
  const [content, setContent] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [orgType, setOrgType] = useState("all"); // all | clinic | hospital
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/site-content").then(({ data }) => setContent(data)); }, []);

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (q) qp.append("q", q);
    if (orgType !== "all") qp.append("org_type", orgType);
    const qs = qp.toString();
    api.get(`/tenants${qs ? `?${qs}` : ""}`).then(({ data }) => {
      setTenants(data);
      setLoading(false);
    });
  }, [q, orgType]);

  const filterPills = [
    { value: "all", label: "All", icon: Building2 },
    { value: "clinic", label: "Clinics", icon: Stethoscope },
    { value: "hospital", label: "Hospitals", icon: Hospital },
  ];

  return (
    <MarketingShell content={content} current="clinics">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-8">
        <span className="label-eyebrow text-mr-accent">Patient sign in · Find your clinic</span>
        <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl text-mr-primary mt-3 max-w-2xl">Pick your clinic to sign in or join.</h1>
        <p className="text-mr-secondary text-lg mt-4 max-w-xl">Tap the clinic or hospital where you're a patient. You'll be able to sign in or create your loyalty account on the next screen.</p>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative max-w-md flex-1">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-mr-secondary" />
            <Input
              data-testid="browse-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clinics by name…"
              className="pl-10 h-12 rounded-full bg-white border-mr shadow-sm"
            />
          </div>
          <div className="inline-flex bg-white border border-mr rounded-full p-1 self-start">
            {filterPills.map((p) => {
              const Icon = p.icon;
              const active = orgType === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  data-testid={`browse-filter-${p.value}`}
                  onClick={() => setOrgType(p.value)}
                  className={`inline-flex items-center gap-1.5 px-4 h-10 text-sm font-medium rounded-full transition-colors ${active ? "bg-mr-primary text-white shadow-sm" : "text-mr-secondary hover:text-mr-primary"}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {p.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        {loading ? (
          <div className="text-mr-secondary py-10 text-center">Loading clinics…</div>
        ) : tenants.length === 0 ? (
          <div className="bg-white border border-mr rounded-2xl p-16 text-center">
            <SearchX className="h-10 w-10 mx-auto text-mr-secondary mb-3" />
            <p className="font-display text-xl font-semibold text-mr-primary">Nothing found</p>
            <p className="text-mr-secondary text-sm mt-2">{q || orgType !== "all" ? "Try a different search or filter." : "No clinics are live on MedRewards yet. Check back soon."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {tenants.map((t, i) => {
              const logo = t.logo_path ? fileUrl(t.logo_path) : null;
              const isHospital = t.org_type === "hospital";
              return (
                <Link
                  key={t.id}
                  to={`/c/${t.slug}`}
                  data-testid={`clinic-card-${t.slug}`}
                  className="group bg-white border border-mr rounded-2xl p-6 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(15,137,100,0.25)] animate-fade-up flex flex-col items-center text-center relative"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <span
                    data-testid={`clinic-card-type-${t.slug}`}
                    className={`absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase ${isHospital ? "bg-mr-accent/10 text-mr-accent" : "bg-mr-primary/10 text-mr-primary"}`}
                  >
                    {isHospital ? <Hospital className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                    {isHospital ? "Hospital" : "Clinic"}
                  </span>
                  <div
                    className="h-20 w-20 rounded-2xl flex items-center justify-center mb-4 overflow-hidden"
                    style={{ background: logo ? "white" : t.brand_color }}
                  >
                    {logo ? (
                      <img src={logo} alt={t.name} className="h-20 w-20 object-cover" />
                    ) : (
                      <span className="font-display font-bold text-white text-2xl">{t.name[0]}</span>
                    )}
                  </div>
                  <h3 className="font-display text-base font-semibold text-mr-primary leading-tight">{t.name}</h3>
                  <p className="text-xs text-mr-secondary mt-1">{t.patient_count.toLocaleString()} {t.patient_count === 1 ? "patient" : "patients"}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-mr-accent opacity-0 group-hover:opacity-100 transition-opacity">
                    Visit <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </MarketingShell>
  );
}
